import { ReactElement, PropsWithChildren, useEffect } from 'react';
import {
  ThemeManager,
  ColorPreset,
  ColorPresetConfig,
  theme as themeManagerTheme
} from 'mt-react-theme-manager';

import {
  PartnerConfig,
  ThemeColor,
  ThemeConfig
} from '../../interfaces/ClientInfo';
import { usePartnerConfig } from '../../contexts/partnerConfig';

// TODO: fix this `any` typing
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const defaultColorConfig: any = {
  primary: {
    base: '#34b44b',
    shadow: '#3dc255',
    dark: '#2da043'
  },
  secondary: {
    base: '#12273f'
  }
};

function themePresetColorsFromPartnerConfig(
  partnerConfig: PartnerConfig | undefined,
  colorConfig: typeof defaultColorConfig
): ColorPresetConfig[] {
  if (!partnerConfig) {
    return [
      {
        colorPreset: 'primary',
        colorMap: colorConfig.primary
      }
    ];
  }

  return Object.keys(themeManagerTheme.intents).reduce((configs, preset) => {
    const partnerConfigKey = `theme${preset[0].toUpperCase()}${preset.substring(
      1
    )}` as keyof ThemeConfig;
    const colors = partnerConfig[partnerConfigKey];

    // skip presets that are not defined in either local config or partner config
    if (!colors && !colorConfig[preset]) return configs;

    // using default config value
    const config = {
      colorPreset: preset as ColorPreset,
      colorMap: {} as ThemeColor
    };

    // replace default value with partner config value,
    if (colors) {
      for (const key in colors) {
        if (!Object.getOwnPropertyDescriptor(colors, key)) continue;

        const presetKey = key as keyof ThemeColor;
        const color = colors[presetKey];

        if (color) config.colorMap[presetKey] = color;
      }
    }

    // if partner config did not define base color, replace everything with default
    if (!config.colorMap.base) config.colorMap = { ...colorConfig[preset] };

    configs.push(config);

    return configs;
  }, [] as ColorPresetConfig[]);
}

interface Props {
  defaultColorConfig?: typeof defaultColorConfig;
}

export default function (props: PropsWithChildren<Props>): ReactElement {
  const { config } = usePartnerConfig();
  const colorConfig = props.defaultColorConfig || defaultColorConfig;

  let colorPresetsConfig = themePresetColorsFromPartnerConfig(
    config,
    colorConfig
  );

  useEffect(() => {
    colorPresetsConfig = themePresetColorsFromPartnerConfig(
      config,
      colorConfig
    );
  }, [config]);

  return (
    <ThemeManager colorPresetsConfig={colorPresetsConfig}>
      {props.children}
    </ThemeManager>
  );
}
